import React, { useCallback } from 'react';
import { View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useFocusEffect, useNavigation } from '@react-navigation/native';

import {
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS,
	PEOPLE_EMPLOYEE_WARNING_STATUS,
	PEOPLE_TRAINING_OUTSTANDING_STATUSES,
} from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';
import { hasPeopleProfileAboutYou } from '../../../common/utils/people/profilePrompts';
import {
	GET_COMPANY_DOCUMENT_ASSIGNMENTS,
} from '../../../common/graphql/people/companyDocumentAssignment';
import {
	GET_EMPLOYEE_DOCUMENT_ASSIGNMENTS,
} from '../../../common/graphql/people/employeeDocumentAssignment';
import { COUNT_MY_WARNINGS, GET_MY_PROFILE_ABOUT_YOU_MOBILE } from '../../graphql/people';
import { GET_MOBILE_TRAINING_ASSIGNMENTS } from '../../graphql/training';
import { NavigationHubMenuItem } from '../../components/navigation';
import { Screen } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const ACTION_REQUIRED_DOCUMENT_STATUSES = [
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.REQUIRED,
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.OVERDUE,
];

const resolveBadgeCount = (count) => {
	const normalized = Number(count) || 0;
	return normalized > 0 ? normalized : null;
};

// Self-service hub for the signed-in user, mirroring the Sentry People My account section.
const MyAccountView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { isAVC } = useUserContext();

	// People self-service is an AVC feature, matching the roster/training drawer gating.
	const canViewPeopleFeatures = Boolean(isAVC);

	const { data: warningCountData, refetch: refetchWarningCount } = useQuery(COUNT_MY_WARNINGS, {
		skip: !canViewPeopleFeatures,
		variables: {
			filter: {
				selfOnly: true,
				status: PEOPLE_EMPLOYEE_WARNING_STATUS.ISSUED,
				acknowledged: false,
			},
		},
		fetchPolicy: 'cache-and-network',
	});

	const { data: companyDocumentData, refetch: refetchCompanyDocuments } = useQuery(GET_COMPANY_DOCUMENT_ASSIGNMENTS, {
		skip: !canViewPeopleFeatures,
		variables: {
			page: 0,
			perPage: 1,
			sortBy: 'dueAt',
			sortDirection: 1,
			filter: { statuses: ACTION_REQUIRED_DOCUMENT_STATUSES },
		},
		fetchPolicy: 'cache-and-network',
	});

	const { data: employeeDocumentData, refetch: refetchEmployeeDocuments } = useQuery(GET_EMPLOYEE_DOCUMENT_ASSIGNMENTS, {
		skip: !canViewPeopleFeatures,
		variables: {
			page: 0,
			perPage: 1,
			sortBy: 'dueAt',
			sortDirection: 1,
			filter: { statuses: ACTION_REQUIRED_DOCUMENT_STATUSES },
		},
		fetchPolicy: 'cache-and-network',
	});

	const { data: trainingData, refetch: refetchTrainings } = useQuery(GET_MOBILE_TRAINING_ASSIGNMENTS, {
		skip: !canViewPeopleFeatures,
		variables: {
			page: 0,
			perPage: 1,
			sortBy: 'dueAt',
			sortDirection: 1,
			filter: { statuses: PEOPLE_TRAINING_OUTSTANDING_STATUSES },
		},
		fetchPolicy: 'cache-and-network',
	});

	const { data: aboutYouData, refetch: refetchAboutYou } = useQuery(GET_MY_PROFILE_ABOUT_YOU_MOBILE, {
		skip: !canViewPeopleFeatures,
		fetchPolicy: 'cache-and-network',
	});

	useFocusEffect(useCallback(() => {
		if (!canViewPeopleFeatures) {
			return;
		}

		refetchAboutYou().catch(() => {});
		refetchWarningCount().catch(() => {});
		refetchCompanyDocuments().catch(() => {});
		refetchEmployeeDocuments().catch(() => {});
		refetchTrainings().catch(() => {});
	}, [
		canViewPeopleFeatures,
		refetchAboutYou,
		refetchCompanyDocuments,
		refetchEmployeeDocuments,
		refetchTrainings,
		refetchWarningCount,
	]));

	// Same rule as the People "Profile photo and about you" action: a photo and at least 1 answer.
	const aboutYouEmployee = aboutYouData?.getMyEmployeeProfile;
	const aboutYouBadge = aboutYouEmployee && !hasPeopleProfileAboutYou(aboutYouEmployee) ? 1 : null;
	const warningsBadge = resolveBadgeCount(warningCountData?.countEmployeeWarnings);
	const documentsBadge = resolveBadgeCount((Number(companyDocumentData?.getCompanyDocumentAssignments?.count) || 0) +
		(Number(employeeDocumentData?.getEmployeeDocumentAssignments?.count) || 0));
	const trainingBadge = resolveBadgeCount((Number(trainingData?.getTrainingAssignments?.count) || 0) +
		(Number(trainingData?.getTrainingAssignments?.mobileOnlineCount) || 0));

	const goTo = useCallback(screen => () => {
		navigation.navigate(screen);
	}, [navigation]);

	return (
		<Screen
			heading="My account"
			subheading="Your details, roster and self-service items in one place."
		>
			<View style={styles.menuList}>
				<NavigationHubMenuItem
					label="Profile"
					description="Photo and contact details"
					icon="person-circle-outline"
					onPress={goTo('ProfileView')}
				/>
				{
					canViewPeopleFeatures && (
						<>
							<NavigationHubMenuItem
								label="Profile photo and about you"
								description="Your photo and get to know you answers"
								icon="happy-outline"
								badge={aboutYouBadge}
								onPress={goTo('MyProfileAboutYouView')}
							/>
							<NavigationHubMenuItem
								label="Employment"
								description="Position, pay and work details"
								icon="briefcase-outline"
								onPress={goTo('MyEmploymentView')}
							/>
							<NavigationHubMenuItem
								label="Roster"
								description="Shifts, availability and leave"
								icon="today-outline"
								onPress={goTo('MyRosterView')}
							/>
							<NavigationHubMenuItem
								label="Warnings"
								description="Issued warnings and acknowledgements"
								icon="alert-circle-outline"
								badge={warningsBadge}
								onPress={goTo('MyWarningsView')}
							/>
							<NavigationHubMenuItem
								label="Documents"
								description="Company documents and your uploads"
								icon="folder-open-outline"
								badge={documentsBadge}
								onPress={goTo('MyDocumentsView')}
							/>
							<NavigationHubMenuItem
								label="Training"
								description="Checklist trainings, sign-offs and online trainings"
								icon="school-outline"
								badge={trainingBadge}
								onPress={goTo('TrainingListView')}
							/>
							<NavigationHubMenuItem
								label="Report incident"
								description="Report an incident, injury or other event"
								icon="megaphone-outline"
								onPress={goTo('ReportIncidentView')}
							/>
						</>
					)
				}
			</View>
		</Screen>
	);
};

const createStyles = ({ ui: { space } }) => ({
	menuList: {
		gap: space.sm,
	},
});

export default MyAccountView;
